import api from "./api";

export interface Product {
  _id: string;
  title: string;
  description: string;
  type: "físico" | "digital" | "taller";
  price: number;
  stock: number;
  // Campos exclusivos de talleres
  tallerInfo?: {
    fechaEvento?: string;
    lugar?: string;
    duracion?: string;
    capacidadMaxima?: number;
    inscripcionesActivas?: boolean;
  };
  image: string; // Deprecado: mantener por compatibilidad
  images: string[]; // Array de URLs o base64
  orientacion?: "horizontal" | "vertical";
  tags: string[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  soldCount?: number;
  featuredOrder?: number; // Orden de destacado (menor = más prioritario)
  // Opciones de tamaño (para productos estándar)
  opcionesTamano?: Array<{
    id: string;
    nombre: string;
    dimensiones: {
      ancho: number;
      alto: number;
      unidad: string;
    };
    orientacion: "horizontal" | "vertical";
    precio: number;
  }>;
  // Campo para nombre personalizado simple (independiente del módulo personalizable)
  permiteNombrePersonalizado?: boolean;
  // Campos para productos personalizables
  esPersonalizable?: boolean;
  slug?: string;
  precioPersonalizado?: number;
  configuracionPersonalizable?: {
    tipoContenido?: "pediatrico" | "lectura" | null;
    contenidoInterno?: Array<{
      id: string;
      titulo: string;
      descripcion?: string;
      imagenUrl: string;
      orden: number;
    }>;
    personalizacion?: {
      permiteEstampados?: boolean;
      categoriaEstampados?: "infantil" | "literario" | "general" | "todos";
      permiteImagenPersonalizada?: boolean;
      permiteLogo?: boolean;
      permiteQRCodes?: boolean;
      campoNombre?: {
        activo: boolean;
        label: string;
        placeholder: string;
        maxCaracteres: number;
      };
      cubierta?: {
        tieneArgollas: boolean;
        urlConArgollas: string;  // Solo para vertical
        urlSinArgollas: string;  // Solo para vertical
        // NOTA: URLs horizontales están hardcodeadas en PersonalizarProducto.tsx
      };
      opcionesTamano?: Array<{
        id: string;
        nombre: string;
        dimensiones: {
          ancho: number;
          alto: number;
          unidad: string;
        };
        orientacion: "horizontal" | "vertical";
        precio: number;
      }>;
    };
  };
}

export interface ProductFilters {
  type?: "físico" | "digital" | "taller" | "";
  minPrice?: number;
  maxPrice?: number;
  tags?: string[];
  search?: string;
  page?: number;
  limit?: number;
  isActive?: boolean | ""; // Permitir string vacío para "Todos"
}

export interface TallerInscripcionData {
  nombre: string;
  email: string;
  celular: string;
}

export interface CreateProductData {
  title: string;
  description?: string;
  type: "físico" | "digital" | "taller";
  price: number;
  stock?: number;
  tallerInfo?: {
    fechaEvento?: string;
    lugar?: string;
    duracion?: string;
    capacidadMaxima?: number;
    inscripcionesActivas?: boolean;
  };
  image?: string; // Deprecado: mantener por compatibilidad
  images: string[]; // Array de URLs o base64
  tags: string[];
  isActive?: boolean;
  featuredOrder?: number; // Orden de destacado (1 = más importante, 2 = segundo, etc.)
}

class ProductService {
  async getProducts(filters?: ProductFilters): Promise<Product[]> {
    const params = new URLSearchParams();
    if (filters?.type) params.append("type", filters.type);
    if (filters?.minPrice)
      params.append("minPrice", filters.minPrice.toString());
    if (filters?.maxPrice)
      params.append("maxPrice", filters.maxPrice.toString());
    /* if (filters?.tags) params.append('tags', filters.tags.join(',')); */
    if (filters?.search) params.append("search", filters.search);
    if (filters?.page) params.append("page", filters.page.toString());
    if (filters?.limit) params.append("limit", filters.limit.toString());

    // Lógica de filtro isActive:
    // - Si isActive es undefined (no se especificó): mostrar solo activos (rutas públicas)
    // - Si isActive es "" (string vacío): mostrar todos (admin panel)
    // - Si isActive es boolean: usar ese valor explícito
    if (filters?.isActive === undefined) {
      // Por defecto en rutas públicas: solo activos
      params.append("isActive", "true");
    } else if (filters?.isActive !== "") {
      // Si se especificó un valor que no es string vacío, usarlo
      params.append("isActive", filters.isActive.toString());
    }
    // Si isActive === "", no se agrega ningún parámetro (muestra todos)

    const response = await api.get(`/products?${params.toString()}`);
    return response.data.data;
  }

  async getProduct(id: string): Promise<Product> {
    const response = await api.get(`/products/${id}`);
    return response.data.data;
  }

  /**
   * Sanitiza los datos del producto antes de enviarlos al backend
   * Soluciona problemas de compatibilidad con Safari y otros navegadores
   */
  private sanitizeProductData(
    data: CreateProductData | Partial<CreateProductData>
  ) {
    const sanitized = { ...data };

    // Sanitizar imágenes: eliminar espacios en blanco y valores vacíos
    if (sanitized.images) {
      sanitized.images = sanitized.images
        .map((img) => img?.trim())
        .filter((img) => img && img.length > 0);

      // Si no quedaron imágenes válidas, eliminar el campo
      if (sanitized.images.length === 0) {
        delete sanitized.images;
      }
    }

    // Sanitizar imagen individual
    if (sanitized.image) {
      sanitized.image = sanitized.image.trim();
      if (!sanitized.image) {
        delete sanitized.image;
      }
    }

    // Sanitizar tags: eliminar espacios y valores vacíos
    if (sanitized.tags) {
      sanitized.tags = sanitized.tags
        .map((tag) => tag?.trim())
        .filter((tag) => tag && tag.length > 0);
    }

    // Sanitizar descripción: eliminar espacios extras
    if (sanitized.description) {
      sanitized.description = sanitized.description.trim();
    }

    // Sanitizar título
    if (sanitized.title) {
      sanitized.title = sanitized.title.trim();
    }

    // Asegurar que stock sea número entero
    if (sanitized.stock !== undefined && sanitized.stock !== null) {
      sanitized.stock = parseInt(String(sanitized.stock), 10);
    }

    // Asegurar que price sea número válido
    if (sanitized.price !== undefined && sanitized.price !== null) {
      sanitized.price = parseFloat(String(sanitized.price));
    }

    // Sanitizar featuredOrder: convertir a número o undefined
    // IMPORTANTE: 0 se trata como "sin orden" (undefined)
    if ("featuredOrder" in sanitized) {
      const value = sanitized.featuredOrder;

      // Convertir a string para verificar si está vacío, luego a número
      const stringValue = String(value);
      const numValue =
        value !== undefined && value !== null && stringValue.trim() !== ""
          ? parseInt(stringValue, 10)
          : 0;

      if (numValue > 0 && !isNaN(numValue)) {
        // Solo mantener si es un número válido mayor a 0
        sanitized.featuredOrder = numValue;
      } else {
        // Eliminar si es 0, negativo, NaN, null, undefined o vacío
        delete sanitized.featuredOrder;
      }
    }

    return sanitized;
  }

  async createProduct(data: CreateProductData): Promise<Product> {
    // Sanitizar datos antes de enviar (soluciona problemas de Safari)
    const sanitizedData = this.sanitizeProductData(data);

    // Log para debugging
    console.log("📤 Enviando producto (sanitizado):", {
      ...sanitizedData,
      images: sanitizedData.images?.map((img) => img.substring(0, 50) + "..."),
    });

    const response = await api.post("/products", sanitizedData);
    return response.data.data;
  }

  async updateProduct(
    id: string,
    data: Partial<Product>
  ): Promise<Product> {
    // Sanitizar solo los campos básicos, preservando el resto (como configuracionPersonalizable)
    const sanitizedData = this.sanitizeProductData(data as any);

    // Preservar campos no sanitizados (como configuracionPersonalizable)
    const finalData = {
      ...data,  // Primero todos los campos originales
      ...sanitizedData  // Luego sobreescribir solo los sanitizados
    };

    console.log("📤 Actualizando producto (con todos los campos):", {
      ...finalData,
      images: finalData.images?.map((img) => img.substring(0, 50) + "..."),
      configuracionPersonalizable: finalData.configuracionPersonalizable
    });
    console.log(
      "🎯 featuredOrder que se enviará:",
      finalData.featuredOrder
    );
    console.log(
      "🎯 cubierta que se enviará:",
      finalData.configuracionPersonalizable?.personalizacion?.cubierta
    );

    const response = await api.put(`/products/${id}`, finalData);
    console.log("📥 Respuesta del backend:", response.data);
    console.log(
      "🎯 featuredOrder en respuesta:",
      response.data.data?.featuredOrder
    );
    return response.data.data;
  }

  async deleteProduct(id: string): Promise<void> {
    await api.delete(`/products/${id}`);
  }

  async getTags(): Promise<string[]> {
    const response = await api.get("/products/tags");
    return response.data.data;
  }

  async inscribirseATaller(productId: string, data: TallerInscripcionData): Promise<{ success: boolean; message: string }> {
    const response = await api.post(`/talleres/${productId}/inscribir`, data);
    return response.data;
  }
}

export default new ProductService();
